import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Check, MessageCircle } from "lucide-react";
import { getIcon } from "../lib/icons";
import { mediaUrl } from "../lib/api";
import { waLink } from "../lib/site";

const accentText = { blue: "text-glg-blue", orange: "text-glg-orange", green: "text-glg-green" };
const accentBorder = {
  blue: "group-hover:border-glg-blue/50",
  orange: "group-hover:border-glg-orange/50",
  green: "group-hover:border-glg-green/50",
};

export const Services = ({ services = [], phone }) => {
  const [active, setActive] = useState(null);

  return (
    <section id="servicios" data-testid="services-section" className="noise relative overflow-hidden bg-glg-black py-24 sm:py-32">
      <div className="absolute inset-0 grid-lines opacity-25" />
      <div className="pointer-events-none absolute right-0 top-1/3 h-96 w-96 rounded-full bg-glg-blue/10 blur-[140px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-glg-blue">Servicios</p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-semibold leading-tight tracking-tight text-white sm:text-5xl">
              Soluciones completas de seguridad y tecnología
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-white/50">
            Diseñamos cada solución a la medida de tu proyecto. Conoce nuestras principales áreas de trabajo.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const Icon = getIcon(s.icon);
            const accent = s.accent || "blue";
            const featured = i === 0;
            return (
              <motion.article
                key={s.id || i}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-glg-surface/60 transition-[transform,border-color] duration-300 hover:-translate-y-1.5 ${accentBorder[accent]} ${featured ? "lg:col-span-2" : ""}`}
              >
                <div className={`relative overflow-hidden ${featured ? "h-60 sm:h-72" : "h-52"}`}>
                  {s.image ? (
                    <img
                      src={mediaUrl(s.image)}
                      alt={s.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-glg-surface to-glg-black">
                      <Icon className={`h-16 w-16 ${accentText[accent]} opacity-40`} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-glg-surface via-glg-surface/40 to-transparent" />
                  <span className="absolute right-4 top-4 font-heading text-sm font-bold tracking-widest text-white/60">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="absolute left-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-glg-black/50 backdrop-blur">
                    <Icon className={`h-5 w-5 ${accentText[accent]}`} />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className={`font-heading font-semibold text-white ${featured ? "text-xl sm:text-2xl" : "text-lg"}`}>{s.name}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-white/55">{s.description}</p>
                  <button
                    onClick={() => setActive(s)}
                    data-testid={`service-more-${i}`}
                    className={`mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${accentText[accent]} transition-opacity hover:opacity-80`}
                  >
                    Ver solución
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="service-modal"
          >
            <div className="absolute inset-0 bg-glg-black/80 backdrop-blur-sm" onClick={() => setActive(null)} />
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.3 }}
              className="relative z-10 max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/10 bg-glg-surface p-8"
            >
              <button
                onClick={() => setActive(null)}
                data-testid="service-modal-close"
                className="absolute right-5 top-5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
              {(() => {
                const Icon = getIcon(active.icon);
                const accent = active.accent || "blue";
                return (
                  <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
                    <Icon className={`h-7 w-7 ${accentText[accent]}`} />
                  </div>
                );
              })()}
              <h3 className="font-heading text-2xl font-semibold text-white">{active.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{active.description}</p>
              {(active.features || []).length > 0 && (
                <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                  {active.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-white/75">
                      <Check className={`h-4 w-4 ${accentText[active.accent || "blue"]}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              )}
              <a
                href={waLink(`Hola GLG Technology, quiero información sobre: ${active.name}.`, phone)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-modal-whatsapp"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-glg-green px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
              >
                <MessageCircle className="h-4 w-4" />
                Consultar por WhatsApp
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
